import { useCallback, useEffect, useRef, useState } from 'react';

export type OptionalParams<T extends unknown[]> = {
    [K in keyof T]: T[K] | undefined;
};

export interface UseApiCallResult<T extends (...params: never[]) => unknown> {
    response: Awaited<ReturnType<T>> | undefined;
    isLoading: boolean;
    error: string | undefined;
    requestApiCall: () => void;
}

const useApiCall = <T extends (...params: never[]) => unknown>(
    apiCall: T,
    // TypeScript doesn't currently provide a clean way to separate required and optional parameters.
    // `requiredParams` defaults to `allParams` to ensure all parameters are treated as required by default.
    allParams: OptionalParams<Parameters<T>>,
    requiredParams: Partial<OptionalParams<Parameters<T>>> = allParams,
    shouldCallInitially = true,
): UseApiCallResult<T> => {
    const [response, setResponse] = useState<Awaited<ReturnType<T>>>();
    const [isLoading, setIsLoading] = useState<boolean>(shouldCallInitially);
    const [error, setError] = useState<string>();
    const [isApiCallRequired, setIsApiCallRequired] =
        useState<boolean>(shouldCallInitially);

    // It is not used to cancel requests, but to prevent stale responses.
    const latestRequestIdRef = useRef(0);

    useEffect(() => {
        const executeApiCall = async (): Promise<void> => {
            const requestId = ++latestRequestIdRef.current;

            try {
                setIsLoading(true);
                setError(undefined);

                const apiCallResponse = await (
                    apiCall as (...args: Parameters<T>) => ReturnType<T>
                )(...(allParams as Parameters<T>));

                if (requestId === latestRequestIdRef.current) {
                    setResponse(apiCallResponse);
                }
            } catch (err: unknown) {
                if (requestId === latestRequestIdRef.current) {
                    setError(
                        (err as Error)?.message ??
                            'Something went wrong while executing the operation.',
                    );
                }
            } finally {
                if (requestId === latestRequestIdRef.current) {
                    setIsLoading(false);
                    setIsApiCallRequired(false);
                }
            }
        };

        const hasAllRequiredParams = requiredParams.every(
            (param) => param !== undefined,
        );

        if (!hasAllRequiredParams) {
            setResponse(undefined);
            setIsLoading(false);
            return;
        }

        if (isApiCallRequired) {
            executeApiCall();
        }
    }, [
        isApiCallRequired,
        apiCall,
        // Parameter arrays are stringified to avoid unnecessary API calls
        JSON.stringify(allParams),
        JSON.stringify(requiredParams),
    ]);

    const requestApiCall = useCallback(() => setIsApiCallRequired(true), []);

    return { response, isLoading, error, requestApiCall };
};

export default useApiCall;
